import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Button, Divider, Header, Icon, Tab } from 'semantic-ui-react';
import _ from 'lodash';
import { bindActionCreators } from 'redux';
import { bindPromiseCreators } from 'redux-saga-routines';
import {
  createLoadingSelector,
  showModal,
  hideModal,
  XTable,
} from 'erisxkit/client';
import PropTypes from 'prop-types';
import { assetTypes } from '../../actions/utilActions';
import {
  ADD_BANK_ACCOUNT,
  ADD_CRYPTO_ADDRESS,
  ADD_COLLATERAL_ACCOUNT,
} from '../../constants/modalTypes';
import bankAccountsMetadata from '../../metadata/bankAccountsMetadata';
import cryptoAddressMetadata from '../../metadata/cryptoAddressMetadata';
import collateralAccountsMetadata from '../../metadata/collateralAccountsMetadata';
import {
  getSelectedUser,
  getUserHasAnyRole,
} from '../../reducers/usersReducer';
import { getSelectedMemberId } from '../../reducers/membersReducer';
import {
  fetchLinkedMemberAssetAccounts,
  createLinkedMemberBankAccountAdmin,
  updateLinkedMemberAssetAccountAdmin,
  createLinkedMemberCryptoAddress,
  createLinkedMemberCollateralAccount,
  getLinkedMemberBankAccounts,
  getLinkedMemberCryptoAddresses,
  deleteLinkedMemberAssetAccount,
  getLinkedMemberCollateralAccounts,
  deleteLinkedMemberAssetAccountPromise,
} from '../../reducers/linkedMemberAssetAccountsReducer';
import {
  generateHashIdPromiseCreator,
  generateCollateralHashIdPromiseCreator,
} from '../../reducers/authReducer';
import { getCryptoAssetTypesAsOptions } from '../../selectors';
import { getEnv } from '../../reducers/envReducer';
import AgGridWrapper from '../../common/table/agGrid/AgGridWrapper';
import BankAccountGrid, {
  gridName,
} from '../../components/agGrid/MemberDetails/BankAccountGrid/BankAccountGrid';
import getColumnDefs from '../../components/agGrid/MemberDetails/BankAccountGrid/columnDefs';
import { CONFIRM_ACTION_WITH_PAYLOAD } from '../../constants/modalTypes';
import { roles } from 'constants/userRoles';

export const SSI_SCREENS_READ_ONLY_ROLES = [roles.RISK];

const mapStateToProps = (state) => ({
  selectedMemberId: getSelectedMemberId(state),
  values: _.get(state.form, ['add_linked_account', 'values'], {}),
  env: getEnv(state),
  selectedUser: getSelectedUser(state),
  assetTypeOptions: getCryptoAssetTypesAsOptions(state),
  linkedMemberBankAccounts: getLinkedMemberBankAccounts(state),
  linkedMemberCryptoAddresses: getLinkedMemberCryptoAddresses(state),
  linkedMemberCollateralAccounts: getLinkedMemberCollateralAccounts(state),
  linkedMemberAssetAccountsLoading: createLoadingSelector([
    fetchLinkedMemberAssetAccounts._PREFIX,
  ])(state), // TODO: proper linked accounts method and loading selector
  isReadOnlyRole: getUserHasAnyRole(state, SSI_SCREENS_READ_ONLY_ROLES),
});

const mapDispatchToProps = (dispatch) => ({
  ...bindPromiseCreators(
    {
      generateHashIdPromiseCreator,
      generateCollateralHashIdPromiseCreator,
      deleteLinkedMemberAssetAccountPromise,
    },
    dispatch,
  ),
  ...bindActionCreators(
    {
      showModal,
      hideModal,
      assetTypes,
      fetchLinkedMemberAssetAccounts,
      createLinkedMemberBankAccountAdmin,
      updateLinkedMemberAssetAccountAdmin,
      createLinkedMemberCryptoAddress,
      createLinkedMemberCollateralAccount,
      deleteLinkedMemberAssetAccount,
    },
    dispatch,
  ),
});

const types = [
  {
    action: 'bank',
    filter: 'bank',
    modal: ADD_BANK_ACCOUNT,
  },
  {
    action: 'crypto address',
    filter: 'crypto',
    modal: ADD_CRYPTO_ADDRESS,
  },
  {
    action: 'collateral SSI',
    filter: 'collateral',
    modal: ADD_COLLATERAL_ACCOUNT,
  },
];

class LinkedMemberAssetAccountsContainer extends Component {
  state = {
    activeIndex: 0,
    selectedLinkedAccount: {},
  };

  componentDidMount = () => {
    this.props.assetTypes();
    this.fetchAssetAccounts();
  };

  handleTabChange = (e, data) =>
    this.setState(
      {
        activeIndex: data.activeIndex,
      },
      () => {
        // on tab switch, get accounts filtered by member and type
        this.fetchAssetAccounts();
      },
    );

  fetchAssetAccounts = () => {
    this.props.fetchLinkedMemberAssetAccounts({
      filter: [
        { attr: 'member_id', op: 'eq', value: this.props.selectedMemberId },
        { attr: 'type', op: 'eq', value: types[this.state.activeIndex].filter },
      ],
    });
  };

  addNew = () => {
    this.props.showModal(types[this.state.activeIndex].modal, {
      hideModal: this.props.hideModal,
      confirm: this.confirm,
      assetTypes: this.props.assetTypeOptions,
      env: this.props.env,
      isAdd: true,
    });
  };

  editSSI = (selectedLinkedAccount) => {
    this.setState({ selectedLinkedAccount });
    this.props.showModal(ADD_BANK_ACCOUNT, {
      hideModal: this.props.hideModal,
      confirm: () => this.edit(),
      assetTypes: this.props.assetTypeOptions,
      env: this.props.env,
      linkedAccount: selectedLinkedAccount,
      isAdd: false,
    });
  };

  confirmBank = (isAdd = true) => {
    const { selectedMemberId } = this.props;
    const {
      accountNumber,
      routingNumber,
      additionalSsi = '',
      accountLabel,
    } = this.props.values;
    const { selectedLinkedAccount = {} } = this.state;

    this.props
      .generateHashIdPromiseCreator({
        accountNumber,
        routingNumber,
      })
      .then((successPayload) => {
        if (isAdd) {
          const payload = {
            ...this.props.values,
            memberId: selectedMemberId,
          };
          this.props.createLinkedMemberBankAccountAdmin(payload);
        } else {
          const payload = {
            id: selectedLinkedAccount.id,
            update: {
              additionalSsi: additionalSsi,
              accountNumber: accountNumber,
              routingNumber: routingNumber,
              label: accountLabel,
              ...this.props.values,
            },
          };
          this.props.updateLinkedMemberAssetAccountAdmin(payload);
        }
        this.props.hideModal();
        this.props.hideModal();
      });
  };

  confirmCrypto = () => {
    const { selectedMemberId } = this.props;
    const payload = {
      ...this.props.values,
      memberId: selectedMemberId,
    };
    this.props.createLinkedMemberCryptoAddress(payload);
    this.props.hideModal();
    this.props.hideModal();
  };

  confirmCollateral = () => {
    const { counterpartyAba, counterpartyCustomer } = this.props.values;
    const { selectedMemberId } = this.props;
    this.props
      .generateCollateralHashIdPromiseCreator({
        counterpartyAba,
        counterpartyCustomer,
      })
      .then(() => {
        const payload = {
          ...this.props.values,
          memberId: selectedMemberId,
        };
        this.props.createLinkedMemberCollateralAccount(payload);
        this.props.hideModal();
        this.props.hideModal();
      });
  };

  confirm = () => {
    if (this.state.activeIndex === 0) {
      this.confirmBank();
    } else if (this.state.activeIndex === 1) {
      this.confirmCrypto();
    } else if (this.state.activeIndex === 2) {
      this.confirmCollateral();
    }
  };

  edit = () => {
    if (this.state.activeIndex === 0) {
      this.confirmBank(false);
    }
  };

  remove = ({ id, label }) => {
    this.props.showModal(CONFIRM_ACTION_WITH_PAYLOAD, {
      header: 'Delete ' + types[this.state.activeIndex].action,
      msg: (
        <p>
          Delete <strong className="mono">{label}</strong>{' '}
          <span>{types[this.state.activeIndex].action}?</span>
        </p>
      ),
      hideModal: this.props.hideModal,
      closeOnDimmerClick: true,
      closeOnEscape: true,
      onConfirm: () => this.deleteSSI(id),
    });
  };

  deleteSSI = (id) => {
    this.props
      .deleteLinkedMemberAssetAccountPromise({
        linkedAccountId: id,
      })
      .then((successPayload) => {
        this.fetchAssetAccounts();
        this.props.hideModal();
      });
  };

  // CO-2325 paginate all SSI in front-end
  tableProps = {
    loading: this.props.linkedMemberAssetAccountsLoading,
    minRows: 3,
    getTdProps: (state, rowInfo, column) => ({
      className: column.id === 'more' && 'td-with-dropdown',
    }),
  };

  panes = [
    // TODO: filter each table by asset type, status, and date added.
    // Date added should be a Flatpickr date range.
    // To be completed as part of CO-2591.
    {
      url: 'bank_accounts',
      menuItem: 'Bank Accounts',
      render: () => (
        <AgGridWrapper
          gridName={gridName}
          agGridComponent={
            <BankAccountGrid
              data={this.props.linkedMemberBankAccounts}
              colDefs={getColumnDefs(
                this.remove,
                this.editSSI,
                this.props.isReadOnlyRole,
              )}
            />
          }
          reactTableComponent={
            <XTable
              data={this.props.linkedMemberBankAccounts}
              columns={bankAccountsMetadata(
                this.remove,
                this.editSSI,
                this.props.isReadOnlyRole,
              )}
              title="BankAccounts"
              {...this.tableProps}
            />
          }
        />
      ),
    },
    {
      url: 'crypto_addresses',
      menuItem: 'Crypto Addresses',
      render: () => (
        <XTable
          data={this.props.linkedMemberCryptoAddresses}
          columns={cryptoAddressMetadata(
            this.remove,
            this.props.isReadOnlyRole,
          )}
          title="BankAccounts"
          {...this.tableProps}
        />
      ),
    },
    {
      url: 'collateral_details',
      menuItem: 'Collateral Details',
      render: () => (
        <XTable
          data={this.props.linkedMemberCollateralAccounts}
          columns={collateralAccountsMetadata(
            this.remove,
            this.props.isReadOnlyRole,
          )}
          title="CollateralAccounts"
          isReadOnlyRole={this.props.isReadOnlyRole}
          {...this.tableProps}
        />
      ),
    },
  ];

  render = () => (
    <Fragment>
      {this.props.memberDetailsView ? (
        <div className="flex-row space-between">
          <Header as="h1" dividing>
            Linked Asset Accounts (SSI)
          </Header>
          <Button
            className="add-button"
            floated="right"
            content="Add SSI"
            icon="plus"
            onClick={this.props.updateMember}
            disabled={this.props.isReadOnlyRole}
          />
        </div>
      ) : (
        <Fragment>
          <div className="flex-row space-between">
            <h4>Linked Asset Accounts (SSI)</h4>
            <Button
              secondary
              onClick={this.addNew}
              className="add-new"
              disabled={this.props.isReadOnlyRole}
            >
              <Icon name="add circle" /> Add a new{' '}
              {types[this.state.activeIndex].action}
            </Button>
          </div>
          <Divider />
        </Fragment>
      )}
      <Tab panes={this.panes} onTabChange={this.handleTabChange} />
    </Fragment>
  );
}

LinkedMemberAssetAccountsContainer.propTypes = {
  assetTypeOptions: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
  assetTypes: PropTypes.func.isRequired,
  createLinkedMemberBankAccountAdmin: PropTypes.func.isRequired,
  createLinkedMemberCryptoAddress: PropTypes.func.isRequired,
  createLinkedMemberCollateralAccount: PropTypes.func.isRequired,
  fetchLinkedMemberAssetAccounts: PropTypes.func.isRequired,
  generateCollateralHashIdPromiseCreator: PropTypes.func.isRequired,
  generateHashIdPromiseCreator: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired,
  linkedMemberAssetAccountsLoading: PropTypes.bool,
  linkedMemberBankAccounts: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      assetType: PropTypes.string.isRequired,
      usableAt: PropTypes.string.isRequired,
      memberId: PropTypes.string.isRequired,
      state: PropTypes.string.isRequired,
      accountNumber: PropTypes.string.isRequired,
      routingNumber: PropTypes.string.isRequired,
      bankName: PropTypes.string.isRequired,
      hashId: PropTypes.string.isRequired,
      isSenAccount: PropTypes.bool.isRequired,
      isSignetAccount: PropTypes.bool.isRequired,
      id: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      isPlaidAccount: PropTypes.bool.isRequired,
    }),
  ),
  linkedMemberCryptoAddresses: PropTypes.arrayOf(
    PropTypes.objectOf(PropTypes.string),
  ),
  linkedMemberCollateralAccounts: PropTypes.arrayOf(
    PropTypes.objectOf(PropTypes.string),
  ),
  memberDetailsView: PropTypes.bool,
  showModal: PropTypes.func.isRequired,
  updateMember: PropTypes.func,
  values: PropTypes.shape({
    isSenAccount: PropTypes.bool,
    isSignetAccount: PropTypes.bool,
    accountLabel: PropTypes.string.isRequired,
    bankName: PropTypes.string.isRequired,
    accountNumber: PropTypes.string.isRequired,
    routingNumber: PropTypes.string.isRequired,
    intermediaryAccountNumber: PropTypes.string,
    intermediaryBankName: PropTypes.string,
    intermediaryBankAddress: PropTypes.string,
    intermediaryRoutingNumber: PropTypes.string,
    additionalSsi: PropTypes.string,
  }),
};

LinkedMemberAssetAccountsContainer.defaultProps = {
  assetTypeOptions: [],
  linkedMemberAssetAccountsLoading: false,
  linkedMemberBankAccounts: [],
  linkedMemberCryptoAddresses: [],
  linkedMemberCollateralAccounts: [],
  memberDetailsView: false,
  values: {},
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(LinkedMemberAssetAccountsContainer);
